import { Box, Grid, Paper, useTheme } from "@mui/material";
import { FC, useMemo } from "react";
import { Link, Outlet } from "react-router-dom";
import { Permissions, UserProjectSummary } from "../../api/dtos";
import GridActions from "../../components/GridActions";
import SetLinks from "../../components/layout/SetLinks";
import { StyledButton } from "../../components/StyledButton";
import CollapsibleAppTable from "../../components/tables/CollapsibleAppTable";
import type { AppColumn } from "../../components/tables/AppTable.types";
import { DefaultProjectCreateLink } from "../../contexts/layouts/LinkContext";
import UserProjectsContext from "../../contexts/UserProjectsContext";
import useUserProjectSummaries from "../../hooks/data/useUserProjectSummaries";

function requiredUserProjectId(row: UserProjectSummary): number {
    if (row.id === undefined || row.id === null) {
        throw new Error("User project summary is missing its ID.");
    }

    return row.id;
}

// @Todo: Figure out how the "all" option for project permissions should be achieved, and also what it actually does

const useUserProjectColumns = () => {
    const agentColumns = useMemo(() => {
        const columns: AppColumn<UserProjectSummary>[] = [
            {
                id: "personnel",
                label: "Personnel",
                scalarType: "text",
                accessor: (row) => row.personnel,
                flex: 1,
            },
            {
                id: "location",
                label: "Location",
                scalarType: "text",
                accessor: (row) => row.location,
                flex: 1,
            },
            {
                id: "actions",
                label: "",
                headerLabel: "",
                kind: "action",
                scalarType: "text",
                accessor: (row) => String(row.id ?? ""),
                width: 100,
                cell: ({ row }) => (
                    <GridActions
                        editPermission={Permissions.AdminAccess}
                        editUrl={`/admin/project-permissions/edit/${requiredUserProjectId(row)}`}
                    />
                ),
            },
        ];

        return columns;
    }, []);

    return agentColumns;
};

const ProjectPermissions: FC = () => {
    const columns = useUserProjectColumns();

    const theme = useTheme();

    const context = useUserProjectSummaries();

    const { data: userprojects, loading: userprojectsLoading } = context;

    const exportMetadata = useMemo(
        () => ({
            fileName: `Project Permissions`,
            exportTitle: `Project Permissions`,
            includeDateTime: true,
        }),
        [],
    );

    return (
        <UserProjectsContext.Provider value={context}>
            <Outlet />
            <SetLinks createProjectLink={DefaultProjectCreateLink} stageLinkEnabled={false} lotLinkEnabled={false} />
            <Grid container gap={theme.spacing(2)} justifyContent={"end"} marginBottom={theme.spacing(2)}>
                <StyledButton
                    variant="contained"
                    //@ts-expect-error
                    component={Link}
                    to={`/admin/project-permissions/create`}
                    state={{
                        back: true,
                    }}
                >
                    Add Project Permission
                </StyledButton>
            </Grid>
            <Paper>
                <Box>
                    <CollapsibleAppTable
                        density="compact"
                        startCollapsed={false}
                        title="Project Permissions"
                        loading={userprojectsLoading}
                        rows={userprojects ?? []}
                        columns={columns}
                        getRowId={requiredUserProjectId}
                        columnControls={{
                            menu: true,
                        }}
                        filterControls
                        export={exportMetadata}
                    />
                </Box>
            </Paper>
        </UserProjectsContext.Provider>
    );
};

export default ProjectPermissions;
